/**
 * Time Conversion Utilities
 * Handles conversion between 12-hour and 24-hour formats
 */

/**
 * Convert 12-hour format to 24-hour format (HH:mm)
 * @param hours - Hour value (1-12)
 * @param minutes - Minute value (0-59)
 * @param period - AM or PM
 * @returns 24-hour format string (HH:mm) or empty string if invalid
 */
export const convert12to24 = (
    hours: number | null,
    minutes: number | null,
    period: 'AM' | 'PM'
): string => {
    if (hours === null || minutes === null) return '';
    
    let hour24 = hours;
    if (period === 'AM' && hours === 12) {
        hour24 = 0; // 12 AM is 00:00
    } else if (period === 'PM' && hours !== 12) {
        hour24 = hours + 12; // PM times except 12 PM
    }
    
    const hoursStr = String(hour24).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    return `${hoursStr}:${minutesStr}`;
};

/**
 * Convert 24-hour format (HH:mm) to 12-hour format
 * @param time24 - Time string in 24-hour format (HH:mm)
 * @returns Object with hours (1-12), minutes (0-59), and period (AM/PM)
 */
export const convert24to12 = (
    time24: string
): { hours: number | null; minutes: number | null; period: 'AM' | 'PM' } => {
    if (!time24) {
        return {
            hours: null,
            minutes: null,
            period: 'AM'
        };
    }
    
    const [ hoursStr, minutesStr ] = time24.split(':');
    const hours24 = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    let hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12; // 0 or 12 should display as 12
    
    const period: 'AM' | 'PM' = hours24 >= 12 ? 'PM' : 'AM';
    
    return {
        hours: hours12,
        minutes,
        period
    };
};

/**
 * Extract time (HH:mm) from UTC ISO string and convert to local time
 * @param input - UTC ISO string (e.g., "2025-12-22T08:30:00.000Z")
 * @returns Local time string in 24-hour format (HH:mm)
 */
export const extractTime = (input: unknown): string => {
    if (!input || typeof input !== 'string') return '';
    
    // Input: YYYY-MM-DDTHH:mm:ss.sssZ
    const [ datePart, timePart ] = input.split('T');
    const [
        yy, mm, dd
    ] = datePart.split('-');

    const isoString = `${yy}-${mm}-${dd}T${timePart}`;

    const date = new Date(isoString); // UTC date
    const local = new Date(
        date.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
    );

    // Extract local time HH:mm
    const hours = String(local.getHours()).padStart(2, '0');
    const minutes = String(local.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
};

/**
 * Normalize time string to Date object for current day
 * @param time - Time string in HH:mm format
 * @returns Date object or empty string if invalid
 */
export const normalizeTime = (time: string): Date | '' => {
    if (!time) return '';

    const [ hours, minutes ] = time.split(':').map(Number);

    const today = new Date();
    today.setHours(hours);
    today.setMinutes(minutes);
    today.setSeconds(0);
    today.setMilliseconds(0);

    return today;
};

/**
 * Convert date to UTC ISO string
 * @param time - Date object or other serializable value
 * @returns UTC ISO string or empty string
 */
export const convertToUtcIso = (time: unknown): string => {
    if (!time) return '';
    return JSON.parse(JSON.stringify(time));
};
